import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useUniversalAuth } from "@west-tennessee-consulting/ui";
import { logger } from "@/lib/logger";
import type {
  CreateConnectionInput,
  MessageActionInput,
  RemoveConnectionInput,
} from "./types";

// Create Connection
export function useCreateConnection() {
  const queryClient = useQueryClient();
  const { session } = useUniversalAuth();

  return useMutation({
    mutationFn: async (connectionData: CreateConnectionInput) => {
      if (!session?.access_token) {
        throw new Error("No authentication token available");
      }

      const response = await fetch("/api/connections/connections", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify(connectionData),
      });

      if (!response.ok) {
        throw new Error("Failed to create connection");
      }

      return response.json();
    },
    onSuccess: () => {
      // Invalidate and refetch connections
      void queryClient.invalidateQueries({ queryKey: ["connections"] });
    },
  });
}

// Remove Connection
export function useRemoveConnection() {
  const queryClient = useQueryClient();
  const { session } = useUniversalAuth();

  return useMutation({
    mutationFn: async ({ connectionId, providerConfigKey }: RemoveConnectionInput) => {
      if (!session?.access_token) {
        throw new Error("No authentication token available");
      }

      const response = await fetch("/api/connections/connections", {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ connectionId, providerConfigKey }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || "Failed to remove connection");
      }

      return response.json();
    },
    onSuccess: () => {
      // Invalidate and force refetch connections immediately, bypassing staleTime
      void queryClient.invalidateQueries({ queryKey: ["connections"] });
      void queryClient.refetchQueries({
        queryKey: ["connections"],
        type: "active", // Only refetch active queries
      });
    },
  });
}

// Message Read/Unread Operations (Placeholder - API endpoints not yet implemented)
export function useMarkMessageAsRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (_input: MessageActionInput) => {
      // TODO: Implement API endpoint for marking messages as read
      logger.warn("Mark message as read not yet implemented");
      return { success: true };
    },
    onSuccess: () => {
      // Invalidate message queries when implemented
      void queryClient.invalidateQueries({ queryKey: ["messages"] });
    },
  });
}

export function useMarkMessageAsUnread() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (_input: MessageActionInput) => {
      // TODO: Implement API endpoint for marking messages as unread
      logger.warn("Mark message as unread not yet implemented");
      return { success: true };
    },
    onSuccess: () => {
      // Invalidate message queries when implemented
      void queryClient.invalidateQueries({ queryKey: ["messages"] });
    },
  });
}
